import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, Undo2, Trash2, FileText, Download, Printer, Eye, Package, User, Phone, Mail, MapPin, ImageIcon } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { toast } from "sonner";
import { buildInvoicePdf, invoiceNoFromOrder } from "@/lib/invoice";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

const PaymentBadge = ({ status }: { status: string }) => {
  const isReceived = status === "received";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium capitalize ${
        isReceived
          ? "bg-success/15 text-success-foreground"
          : "bg-warning/15 text-warning-foreground"
      }`}
    >
      {isReceived ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {status}
    </span>
  );
};

const ProductThumb = ({ src, alt }: { src?: string | null; alt: string }) => (
  <div className="h-11 w-11 rounded-lg overflow-hidden bg-muted shrink-0 ring-1 ring-border grid place-items-center">
    {src ? (
      <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" />
    ) : (
      <ImageIcon className="h-4 w-4 text-muted-foreground" />
    )}
  </div>
);

const pickThumb = (item: any) =>
  item?.product_image ||
  item?.products?.image_url ||
  (Array.isArray(item?.products?.images) ? item.products.images[0] : null) ||
  null;

const AdminOrders = () => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [previewByOrder, setPreviewByOrder] = useState<Record<string, { first: any; count: number }>>({});
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [undoId, setUndoId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    const { data: orderData } = await supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false });
    const list = orderData ?? [];
    setOrders(list);
    setLoading(false);

    if (list.length) {
      const ids = list.map((o) => o.id);
      const { data: itemData } = await supabase
        .from("order_items")
        .select("id, order_id, product_name, quantity, product_image, products(image_url, images)")
        .in("order_id", ids);
      const map: Record<string, { first: any; count: number }> = {};
      (itemData ?? []).forEach((it: any) => {
        if (!map[it.order_id]) map[it.order_id] = { first: it, count: 1 };
        else map[it.order_id].count += 1;
      });
      setPreviewByOrder(map);
    }
  };
  useEffect(() => { load(); }, []);

  const totalRevenue = orders
    .filter((o) => o.payment_status === "received")
    .reduce((s, o) => s + Number(o.total_amount), 0);
  const pendingCount = orders.filter((o) => o.payment_status !== "received").length;

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("orders").update({ order_status: status }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Status updated");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, order_status: status } : o)));
  };

  const markReceived = async () => {
    if (!confirmId) return;
    setMarking(true);
    const receivedAt = new Date().toISOString();
    const { error } = await supabase
      .from("orders")
      .update({ payment_status: "received", received_at: receivedAt })
      .eq("id", confirmId);
    setMarking(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment marked as received");
    setOrders((prev) =>
      prev.map((o) =>
        o.id === confirmId ? { ...o, payment_status: "received", received_at: receivedAt } : o,
      ),
    );
    setConfirmId(null);
  };

  const undoReceived = async () => {
    if (!undoId) return;
    setMarking(true);
    const { error } = await supabase
      .from("orders")
      .update({ payment_status: "pending", received_at: null })
      .eq("id", undoId);
    setMarking(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment reverted to pending");
    setOrders((prev) =>
      prev.map((o) =>
        o.id === undoId ? { ...o, payment_status: "pending", received_at: null } : o,
      ),
    );
    setUndoId(null);
  };

  const viewDetails = async (order: any) => {
    setSelected(order);
    setItems([]);
    setItemsLoading(true);
    const { data } = await supabase
      .from("order_items")
      .select("*, products(image_url, images, category, categories(name))")
      .eq("order_id", order.id);
    setItems(data ?? []);
    setItemsLoading(false);
  };

  const buildInvoiceFromOrder = (order: any, orderItems: any[]) => ({
    invoiceNo: invoiceNoFromOrder(order),
    date: new Date(order.created_at).toLocaleDateString("en-GB"),
    orderId: order.id,
    paymentMethod: order.payment_method,
    paymentStatus: order.payment_status,
    orderStatus: order.order_status,
    customer: {
      name: order.customer_name,
      phone: order.customer_phone,
      email: order.customer_email,
      address: order.customer_address,
    },
    items: orderItems.map((i) => ({
      name: i.product_name,
      qty: Number(i.quantity),
      price: Number(i.price),
      color: i.color ?? null,
      size: i.size ?? null,
      image: pickThumb(i),
    })),
    shipping: Number(order.shipping_charge) || 0,
    discount: 0,
    tax: 0,
    notes: undefined,
  });

  const downloadInvoice = async () => {
    if (!selected) return;
    try {
      const doc = await buildInvoicePdf(buildInvoiceFromOrder(selected, items));
      doc.save(`Invoice-${selected.id}.pdf`);
      toast.success("Invoice downloaded");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate invoice");
    }
  };

  const printInvoice = async () => {
    if (!selected) return;
    try {
      const doc = await buildInvoicePdf(buildInvoiceFromOrder(selected, items));
      doc.autoPrint();
      const url = doc.output("bloburl");
      const w = window.open(url, "_blank");
      if (!w) toast.error("Allow pop-ups to print");
    } catch (e: any) {
      toast.error(e.message || "Failed to print invoice");
    }
  };

  const deleteOrder = async () => {
    if (!deleteId) return;
    setDeleting(true);
    await supabase.from("order_items").delete().eq("order_id", deleteId);
    const { error } = await supabase.from("orders").delete().eq("id", deleteId);
    setDeleting(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Order deleted");
    setOrders((prev) => prev.filter((o) => o.id !== deleteId));
    setDeleteId(null);
  };

  const itemsSubtotal = items.reduce((s, i) => s + Number(i.price) * Number(i.quantity), 0);
  const discount = selected
    ? Math.max(0, Number(selected.subtotal) + Number(selected.shipping_charge) - Number(selected.total_amount))
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Orders</h1>
        <p className="text-sm text-muted-foreground">Manage customer orders and payments</p>
      </div>

      {/* Revenue summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Received revenue</p>
          <p className="font-display text-2xl font-bold mt-1">{formatCurrency(totalRevenue, symbol)}</p>
        </Card>
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending payments</p>
          <p className="font-display text-2xl font-bold mt-1">{pendingCount}</p>
        </Card>
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total orders</p>
          <p className="font-display text-2xl font-bold mt-1">{orders.length}</p>
        </Card>
      </div>

      <Card className="border-0 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4 min-w-[220px]">Products</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4 hidden md:table-cell">Date</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Payment</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={8} className="p-4"><Skeleton className="h-20" /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No orders yet.</td></tr>
              ) : (
                orders.map((o) => {
                  const received = o.payment_status === "received";
                  const stop = (e: React.MouseEvent) => e.stopPropagation();
                  const preview = previewByOrder[o.id];
                  const first = preview?.first;
                  const extra = preview ? preview.count - 1 : 0;
                  return (
                    <tr
                      key={o.id}
                      onClick={() => viewDetails(o)}
                      className="hover:bg-muted/30 transition-smooth cursor-pointer"
                    >
                      <td className="p-4 align-top">
                        <span className="font-mono text-xs text-primary">#{o.id.slice(0, 8)}</span>
                        <p className="text-[10px] text-muted-foreground md:hidden mt-1">
                          {new Date(o.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="p-4 align-top">
                        {first ? (
                          <div className="flex items-center gap-3">
                            <ProductThumb src={pickThumb(first)} alt={first.product_name} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate max-w-[200px]">{first.product_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Qty: {first.quantity}
                                {extra > 0 && <span className="ml-2 text-primary">+{extra} more item{extra > 1 ? "s" : ""}</span>}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-4 align-top">
                        <p className="text-sm font-medium">{o.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{o.customer_phone}</p>
                      </td>
                      <td className="p-4 align-top text-sm text-muted-foreground hidden md:table-cell">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-4 align-top text-right font-medium">{formatCurrency(Number(o.total_amount), symbol)}</td>
                      <td className="p-4 align-top"><PaymentBadge status={o.payment_status} /></td>
                      <td className="p-4 align-top" onClick={stop}>
                        <Select value={o.order_status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 align-top text-right" onClick={stop}>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewDetails(o)}
                            className="h-8 text-xs gap-1"
                          >
                            <Eye className="h-3 w-3" /> View
                          </Button>
                          {received ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setUndoId(o.id)}
                              className="h-8 text-xs gap-1"
                            >
                              <Undo2 className="h-3 w-3" /> Undo
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="accent"
                              onClick={() => setConfirmId(o.id)}
                              className="h-8 text-xs"
                            >
                              Receive
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteId(o.id)}
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            aria-label="Delete order"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2 pr-6">
              <span className="flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                Order #{selected ? selected.id.slice(0, 8) : ""}
              </span>
              {selected && <PaymentBadge status={selected.payment_status} />}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5 animate-fade-in">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={downloadInvoice} className="gap-2" disabled={items.length === 0}>
                  <Download className="h-4 w-4" /> Download Invoice
                </Button>
                <Button size="sm" variant="outline" onClick={printInvoice} className="gap-2" disabled={items.length === 0}>
                  <Printer className="h-4 w-4" /> Print
                </Button>
                <div className="ml-auto text-xs text-muted-foreground self-center">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>

              {/* Customer card */}
              <div className="rounded-xl border bg-card p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                  <User className="h-3.5 w-3.5" /> Customer information
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="font-medium">{selected.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{selected.customer_phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium break-all">{selected.customer_email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 sm:col-span-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Shipping address</p>
                      <p className="font-medium">{selected.customer_address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ordered products */}
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <Package className="h-3.5 w-3.5" /> Ordered products
                  </p>
                  <span className="text-xs text-muted-foreground">{items.length} item{items.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="divide-y">
                  {itemsLoading ? (
                    <div className="p-4"><Skeleton className="h-16" /></div>
                  ) : items.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">No items found</div>
                  ) : (
                    items.map((i, idx) => {
                      const thumb = pickThumb(i);
                      const categoryName = i.products?.categories?.name || i.products?.category;
                      const lineTotal = Number(i.price) * Number(i.quantity);
                      return (
                        <div key={i.id} className="flex gap-3 p-4">
                          <div className="text-xs text-muted-foreground font-mono w-5 pt-1">{idx + 1}.</div>
                          <ProductThumb src={thumb} alt={i.product_name} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm leading-tight">{i.product_name}</p>
                            {categoryName && (
                              <p className="text-xs text-muted-foreground mt-0.5">{categoryName}</p>
                            )}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {i.color && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted">
                                  <span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-foreground/15" style={{ background: i.color }} />
                                  {i.color}
                                </span>
                              )}
                              {i.size && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-muted">Size: {i.size}</span>
                              )}
                              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                                Qty: {i.quantity}
                              </span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs text-muted-foreground">{formatCurrency(Number(i.price), symbol)} × {i.quantity}</p>
                            <p className="font-semibold mt-1">{formatCurrency(lineTotal, symbol)}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Order summary */}
              <div className="rounded-xl border bg-card p-4 space-y-2 text-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Order summary</p>
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatCurrency(Number(selected.subtotal) || itemsSubtotal, symbol)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{formatCurrency(Number(selected.shipping_charge), symbol)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-success"><span>Discount</span><span>− {formatCurrency(discount, symbol)}</span></div>
                )}
                <div className="border-t pt-2 flex justify-between font-display text-lg font-bold">
                  <span>Grand Total</span>
                  <span>{formatCurrency(Number(selected.total_amount), symbol)}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 pt-2 text-xs text-muted-foreground border-t mt-2">
                  <span>Order status: <span className="text-foreground capitalize font-medium">{selected.order_status}</span></span>
                  {selected.payment_method && <span>Payment: <span className="text-foreground font-medium">{selected.payment_method}</span></span>}
                  {selected.received_at && <span>Received: <span className="text-foreground">{new Date(selected.received_at).toLocaleString()}</span></span>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark payment as received?</AlertDialogTitle>
            <AlertDialogDescription>
              This will add the order amount to your total revenue. You can undo this later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={marking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={markReceived} disabled={marking}>
              {marking ? "Marking..." : "Mark as received"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!undoId} onOpenChange={(o) => !o && setUndoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to undo this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              The payment status will revert to <strong>Pending</strong> and the amount will be removed from your revenue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={marking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={undoReceived} disabled={marking}>
              {marking ? "Reverting..." : "Yes, undo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order and all its items from your records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteOrder}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminOrders;
